// Generateur TSPL pour le ticket centralise (App Employe -> relais -> imprimante
// partagee). Reproduit la disposition du ticket navigateur existant (voir
// apps/admin/src/lib/labels.js + styles.css : description / reference /
// code-barres / zone en bas), mais en commandes TSPL brutes au lieu de
// HTML+CSS, car la connexion HTTPS directe navigateur->imprimante ne passe
// pas par un moteur de rendu.
//
// HYPOTHESES A VERIFIER SUR L'IMPRIMANTE REELLE (voir cahier-des-charges.md
// §12) : resolution 203 dpi (8 points/mm). La police TSPL "1" a deja ete
// recalibree plusieurs fois sur des tickets de test reels (hauteur 12->24,
// largeur 8->16 puis jugee encore trop grande) -- la largeur de caractere
// est desormais un reglage admin (tspl_largeur_caractere, voir "Format
// d'etiquette") plutot qu'une constante ici : impossible a verifier a
// distance sans imprimante reelle, donc ajustable sur le terrain sans
// redeploiement. DOTS_PER_MM / FONT_CHAR_H restent des constantes -- a
// ajuster ici si le ticket imprime sort encore mal aligne verticalement.

const DOTS_PER_MM = 8;
const DEFAULT_FONT_CHAR_W = 12;
const FONT_CHAR_H = 24;
const MARGIN_DOTS = 8;
const LINE_GAP = 4;
const BARCODE_NARROW_DOTS = 2; // doit rester egal au parametre "narrow" passe a BARCODE ci-dessous
const LINE_HEIGHT = FONT_CHAR_H + LINE_GAP;
// Ligne des chiffres lisibles que BARCODE dessine sous les barres (readable=1) :
// traitee comme une rangee a part -- plusieurs tickets de test reels ont
// confirme qu'il n'y a plus de chevauchement avec cette marge.
const BARCODE_TEXT_RESERVED = FONT_CHAR_H + LINE_GAP + 3;
// Hauteur du code-barres : 33% de la hauteur du ticket (cahier-des-charges
// §12). Reference + zone tiennent desormais sur UNE seule rangee combinee
// sous le code-barres (voir buildLabelBlock), ce qui a libere assez de place
// pour remettre le code-barres a sa hauteur pleine sans rien sacrifier.
const BARCODE_HEIGHT_RATIO = 0.33;

function wrapLines(text, maxCharsPerLine, maxLines) {
  if (maxLines <= 0) return [];
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxCharsPerLine && current) {
      lines.push(current);
      current = word;
      if (lines.length === maxLines) break;
    } else {
      current = candidate;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  return lines.slice(0, maxLines);
}

function centeredX(text, widthDots, fontCharWidth) {
  const textWidth = text.length * fontCharWidth;
  return Math.max(0, Math.round((widthDots - textWidth) / 2));
}

// TSPL n'a pas d'attribut "gras" pour les polices bitmap integrees -- on
// simule en dessinant le texte deux fois, decale d'1 point horizontalement
// (technique standard sur ce type de langage d'etiquette). Reserve a la
// description, comme le font-weight:750 de ticket-desc dans le CSS admin.
function boldTextLine(x, y, content) {
  return [
    `TEXT ${x},${y},"1",0,1,1,"${content}"`,
    `TEXT ${x + 1},${y},"1",0,1,1,"${content}"`,
  ].join('\r\n');
}

// Estimation de la largeur rendue d'un CODE128. Hypothese : sur une chaine
// 100% numerique, l'imprimante bascule en sous-ensemble C (2 chiffres par
// symbole) ; s'il reste un chiffre seul (longueur impaire), 1 symbole
// supplementaire est ajoute. Chaque symbole fait 11 modules, + 13 modules
// pour le caractere stop. Le decalage residuel se corrige via le reglage
// admin tspl_barcode_decalage_x (voir barcodeOffsetX plus bas).
function estimateBarcodeWidthDots(content) {
  const digits = (content || '').replace(/[^0-9]/g, '');
  const isAllDigits = digits.length === (content || '').length && digits.length > 0;
  let symbols;
  if (isAllDigits) {
    const pairs = Math.floor(digits.length / 2);
    const oddExtra = digits.length % 2 ? 1 : 0;
    symbols = 2 + pairs + oddExtra; // start + checksum + paires (+ 1 si impair)
  } else {
    symbols = 2 + (content || '').length; // sous-ensemble B : 1 caractere par symbole
  }
  const modules = symbols * 11 + 13; // + caractere stop
  return modules * BARCODE_NARROW_DOTS;
}

function escapeTspl(text) {
  return (text || '').replace(/"/g, '');
}

// Construit le bloc de dessin d'UN ticket (CLS + TEXT/BARCODE + PRINT 1).
// Ordre : description (toujours 2 lignes reservees, en gras) -> code-barres
// (hauteur pleine, 33%) -> reference + zone COMBINEES sur une seule rangee
// (reference alignee a gauche, zone alignee a droite). Rien n'est jamais
// masque : mettre reference et zone sur la meme ligne libere exactement la
// place qui manquait pour garder les 4 elements en meme temps sur un ticket
// 50x25mm.
function buildLabelBlock({ description, reference, zoneName, barcode, widthMm, heightMm, barcodeOffsetX, fontCharWidth }) {
  const charW = Number(fontCharWidth) > 0 ? Number(fontCharWidth) : DEFAULT_FONT_CHAR_W;
  const widthDots = Math.round(widthMm * DOTS_PER_MM);
  const heightDots = Math.round(heightMm * DOTS_PER_MM);
  const barcodeHeightDots = Math.round(heightMm * BARCODE_HEIGHT_RATIO * DOTS_PER_MM);
  const maxCharsPerLine = Math.max(4, Math.floor((widthDots - 2 * MARGIN_DOTS) / charW));

  const hasRef = Boolean((reference || '').trim());
  const hasZone = Boolean((zoneName || '').trim());
  const footerReserved = (hasRef || hasZone) ? LINE_HEIGHT : 0;
  const barcodeReserved = barcodeHeightDots + BARCODE_TEXT_RESERVED;

  // Description : toujours 2 lignes pleines reservees (comme le
  // min-height: 2.2em de ticket-desc dans le CSS admin) -- jamais calculee a
  // partir de ce qu'il reste, sinon un nom long se fait tronquer silencieusement.
  const descRowHeight = 2 * LINE_HEIGHT;
  const descLines = wrapLines(description, maxCharsPerLine, 2);

  // Reference tronquee pour laisser la place a la zone sur la meme rangee
  // (zone = emplacement terrain, prioritaire ; reference redondante avec le
  // code-barres, donc sacrifiee en premier si la ligne est trop courte).
  const zoneReserveChars = hasZone ? zoneName.trim().length + 2 : 0;
  const refMaxChars = Math.max(4, maxCharsPerLine - zoneReserveChars);
  const refLine = hasRef ? reference.trim().slice(0, refMaxChars) : '';
  const zoneLine = hasZone ? zoneName.trim().slice(0, maxCharsPerLine) : '';

  const out = ['CLS'];

  // Ligne(s) de description centrées dans leur rangée réservée.
  const descTextHeight = descLines.length * LINE_HEIGHT;
  let y = MARGIN_DOTS + Math.max(0, Math.round((descRowHeight - descTextHeight) / 2));
  for (const line of descLines) {
    out.push(boldTextLine(centeredX(line, widthDots, charW), y, escapeTspl(line)));
    y += LINE_HEIGHT;
  }

  // Code-barres juste après la rangée description (hauteur fixe, réservée en
  // entier que la designation l'utilise ou non -- voir plus haut).
  y = MARGIN_DOTS + descRowHeight;

  const barcodeWidthDots = estimateBarcodeWidthDots(barcode);
  const naturalX = Math.round((widthDots - barcodeWidthDots) / 2);
  const barcodeX = Math.max(MARGIN_DOTS, naturalX + Math.round(Number(barcodeOffsetX) || 0));
  out.push(`BARCODE ${barcodeX},${y},"128",${barcodeHeightDots},1,0,${BARCODE_NARROW_DOTS},${BARCODE_NARROW_DOTS},"${escapeTspl(barcode)}"`);
  y += barcodeReserved;

  // Reference (gauche) + zone (droite) sur la meme rangee.
  if (refLine) {
    out.push(`TEXT ${MARGIN_DOTS},${y},"1",0,1,1,"${escapeTspl(refLine)}"`);
  }
  if (zoneLine) {
    const zoneX = Math.max(MARGIN_DOTS, widthDots - MARGIN_DOTS - zoneLine.length * charW);
    out.push(`TEXT ${zoneX},${y},"1",0,1,1,"${escapeTspl(zoneLine)}"`);
  }
  y += footerReserved;

  out.push('PRINT 1');
  return out.join('\r\n');
}

export function buildTicketTspl({ description, reference, zoneName, barcode, widthMm, heightMm, quantity, barcodeOffsetX, fontCharWidth }) {
  const count = Math.max(1, Math.round(quantity));
  const header = [`SIZE ${widthMm} mm,${heightMm} mm`, 'GAP 2 mm,0'];
  const block = buildLabelBlock({ description, reference, zoneName, barcode, widthMm, heightMm, barcodeOffsetX, fontCharWidth });
  // Repete le bloc CLS+dessin+PRINT 1 `count` fois plutot que de s'appuyer sur
  // un unique "PRINT n" : les dialectes TSPL varient sur la semantique exacte
  // du parametre de copies, alors qu'un PRINT 1 repete est sans ambiguite --
  // corrige un ecart observe entre quantite demandee et quantite imprimee.
  const body = Array.from({ length: count }, () => block).join('\r\n');
  return [...header, body].join('\r\n') + '\r\n';
}
